import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Plus,
  Edit,
  Trash2,
  MapPin,
  X,
  Save,
  ChevronsUpDown,
  Check,
  Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import RoomAvailabilityForm from "./RoomAvailabilityForm";

// Multi-select component for equipment assignment
const EquipmentMultiSelect = ({
  allEquipment = [],
  assignedEquipmentIds = [],
  onSelectionChange,
  disabled,
}) => {
  const [open, setOpen] = useState(false);

  // Equipment that is unassigned or assigned to the current room
  const availableEquipment = allEquipment.filter(
    (eq) => !eq.assigned_room_id || assignedEquipmentIds.includes(eq.id)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
          data-testid="room-equipment-select"
        >
          {assignedEquipmentIds.length > 0
            ? `${assignedEquipmentIds.length} item(s) selected`
            : "Select equipment..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder="Search equipment..." />
          <CommandEmpty>No equipment found.</CommandEmpty>
          <CommandGroup className="max-h-60 overflow-y-auto">
            {availableEquipment.map((equipment) => (
              <CommandItem
                key={equipment.id}
                data-testid={`room-equipment-option-${equipment.name.toLowerCase().replace(/\s+/g, '-')}`}
                onSelect={() => {
                  const newSelection = assignedEquipmentIds.includes(
                    equipment.id
                  )
                    ? assignedEquipmentIds.filter((id) => id !== equipment.id)
                    : [...assignedEquipmentIds, equipment.id];
                  onSelectionChange(newSelection);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    assignedEquipmentIds.includes(equipment.id)
                      ? "opacity-100"
                      : "opacity-0"
                  )}
                />
                {equipment.name}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default function RoomManagement() {
  const [showForm, setShowForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const queryClient = useQueryClient();

  // Fetch rooms using React Query
  const { data: rooms = [], isLoading } = useQuery({
    queryKey: ["rooms"],
    queryFn: () => apiClient.get(ENDPOINTS.SETTINGS.ROOMS),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch equipment using React Query
  const { data: allEquipment = [] } = useQuery({
    queryKey: ["equipment"],
    queryFn: () => apiClient.get(ENDPOINTS.SETTINGS.EQUIPMENT),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const [showAvailabilityForm, setShowAvailabilityForm] = useState(false);
  const [availabilityRoom, setAvailabilityRoom] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    equipment_ids: [],
    status: "active",
  });

  // Mutations for room operations
  const createRoomMutation = useMutation({
    mutationFn: (roomData) => apiClient.post(ENDPOINTS.SETTINGS.ROOMS, roomData),
    onSuccess: () => {
      queryClient.invalidateQueries(["rooms"]);
      resetForm();
    },
  });

  const updateRoomMutation = useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`${ENDPOINTS.SETTINGS.ROOMS}/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["rooms"]);
      resetForm();
    },
  });

  const deleteRoomMutation = useMutation({
    mutationFn: (roomId) => apiClient.delete(`${ENDPOINTS.SETTINGS.ROOMS}/${roomId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["rooms"]);
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      equipment_ids: [],
      status: "active",
    });
    setEditingRoom(null);
    setShowForm(false);
  };

  const handleAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (room) => {
    setFormData({
      name: room.name || "",
      equipment_ids: room.equipment_ids || [],
      status: room.status || "active",
    });
    setEditingRoom(room);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (editingRoom) {
      updateRoomMutation.mutate({ id: editingRoom.id, data: formData });
    } else {
      createRoomMutation.mutate(formData);
    }
  };

  const handleDelete = async (roomId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this room?"
      )
    ) {
      deleteRoomMutation.mutate(roomId);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAvailabilitySettings = (room) => {
    setAvailabilityRoom(room);
    setShowAvailabilityForm(true);
  };

  const handleSaveAvailability = async (roomData) => {
    updateRoomMutation.mutate({ id: roomData.id, data: roomData });
    setShowAvailabilityForm(false);
    setAvailabilityRoom(null);
  };

  const statusColors = {
    active: "bg-green-100 text-green-700",
    maintenance: "bg-orange-100 text-orange-700",
    inactive: "bg-gray-100 text-gray-700",
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Room Management
            </CardTitle>
            <Button onClick={handleAdd} className="gap-2" data-testid="room-add-btn">
              <Plus className="w-4 h-4" />
              Add Room
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showForm && (
            <div className="mb-6 p-4 border rounded-lg bg-slate-50">
              <form onSubmit={handleSave} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Room Name *</Label>
                    <Input
                      id="name"
                      data-testid="room-name-input"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="e.g., Examination Room 1"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) =>
                        handleInputChange("status", value)
                      }
                    >
                      <SelectTrigger data-testid="room-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active" data-testid="room-status-active">Active</SelectItem>
                        <SelectItem value="maintenance" data-testid="room-status-maintenance">Maintenance</SelectItem>
                        <SelectItem value="inactive" data-testid="room-status-inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="equipment_ids">Assign Equipment</Label>
                  <EquipmentMultiSelect
                    allEquipment={allEquipment}
                    assignedEquipmentIds={formData.equipment_ids || []}
                    onSelectionChange={(ids) =>
                      handleInputChange("equipment_ids", ids)
                    }
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="room-save-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {editingRoom ? "Update" : "Add"} Room
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-x-auto -mx-6 px-6">
            <div className="min-w-full inline-block align-middle">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[150px]">Name</TableHead>
                    <TableHead className="min-w-[100px]">Equipment</TableHead>
                    <TableHead className="min-w-[100px]">Status</TableHead>
                    <TableHead className="min-w-[200px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan="4" className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : (
                    rooms.map((room, index) => (
                      <TableRow key={room.id} data-testid={`room-row-${index}`}>
                        <TableCell className="font-medium">
                          {room.name}
                        </TableCell>
                        <TableCell>
                          {(room.equipment_ids || []).length} items
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`${statusColors[room.status]} border-0 whitespace-nowrap`}
                          >
                            {room.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAvailabilitySettings(room)}
                              title="Configure Availability"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`room-availability-btn-${room.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <Clock className="w-4 h-4 mr-1" />
                              Availability
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(room)}
                              title="Edit Room"
                              data-testid={`edit-room-${index}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(room.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete Room"
                              data-testid={`delete-room-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Room Availability Form */}
      {showAvailabilityForm && (
        <RoomAvailabilityForm
          room={availabilityRoom}
          onSave={handleSaveAvailability}
          onClose={() => {
            setShowAvailabilityForm(false);
            setAvailabilityRoom(null);
          }}
        />
      )}
    </div>
  );
}
